import { useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Smart date input — typing-first with optional calendar fallback.
 *
 * The native `<input type="date">` is fine on desktop but TERRIBLE
 * on mobile when picking distant birthdays — you have to scroll
 * through years one at a time. Most adults can type "15.06.1990"
 * faster than they can find 1990 in a wheel picker.
 *
 * UX:
 *   • Default: a text input with auto-dot formatting (DD.MM.YYYY).
 *   • numeric keyboard pops up on mobile via `inputmode="numeric"`.
 *   • Tap 📅 icon to flip to a native date picker for visual selection.
 *
 * Backend accepts both DD.MM.YYYY and YYYY-MM-DD (see
 * `app/api/v1/profile.py::parse_flexible_date`), so either mode works.
 */

function autoFormat(raw: string): string {
  // Allow user to paste/type ISO (YYYY-MM-DD) — keep as-is.
  if (/^\d{4}-\d{1,2}-?\d{0,2}$/.test(raw)) return raw.slice(0, 10);

  // Otherwise strip non-digits and re-format into DD.MM.YYYY
  const digits = raw.replace(/\D/g, '').slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 4)}.${digits.slice(4)}`;
}

interface Props {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  /** Tailwind classes for the text input wrapper. */
  className?: string;
  /** Optional className for the input itself; defaults to mystic-input style. */
  inputClassName?: string;
  autoFocus?: boolean;
}

export default function DateField({
  value, onChange, placeholder,
  className = '', inputClassName, autoFocus,
}: Props) {
  const { t } = useTranslation();
  const [calendarMode, setCalendarMode] = useState(false);
  const ph = placeholder ?? t('fields.date_placeholder');

  // For calendar mode we need YYYY-MM-DD; convert if user typed DD.MM.YYYY.
  const isoValue = (() => {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(value);
    if (m) return `${m[3]}-${m[2]}-${m[1]}`;
    return '';
  })();

  return (
    <div className={`relative flex items-center gap-2 ${className}`}>
      {calendarMode ? (
        <input
          type="date"
          value={isoValue}
          onChange={(e) => onChange(e.target.value)}
          className={
            inputClassName ??
            'w-full rounded-lg border border-gold-500/30 bg-night-900/60 ' +
            'px-3 py-2 font-serif text-base text-gold-100 outline-none ' +
            'transition focus:border-gold-400'
          }
          autoFocus={autoFocus}
        />
      ) : (
        <input
          type="text"
          inputMode="numeric"
          value={value}
          onChange={(e) => onChange(autoFormat(e.target.value))}
          placeholder={ph}
          maxLength={10}
          className={
            inputClassName ??
            'w-full rounded-lg border border-gold-500/30 bg-night-900/60 ' +
            'px-3 py-2 font-serif text-base text-gold-100 outline-none ' +
            'transition placeholder:text-gold-300/35 focus:border-gold-400'
          }
          autoFocus={autoFocus}
        />
      )}
      <button
        type="button"
        onClick={() => setCalendarMode(!calendarMode)}
        aria-label="switch input mode"
        className="shrink-0 rounded-lg border border-gold-500/30 px-2.5 py-2
                   text-base text-gold-300/80 transition
                   hover:border-gold-400 hover:text-gold-100"
        title={calendarMode ? t('fields.switch_to_typing') : t('fields.switch_to_picker_date')}
      >
        {calendarMode ? '⌨' : '📅'}
      </button>
    </div>
  );
}
